"use client";

import { memo, Suspense, useCallback, useEffect, useMemo, useRef } from "react";

import BFLCarouselNew from "../Carousel/Carousel";
import { DynamicSkeletonLoader, Skeleton } from "../SkeletonLoader/SkeletonLoader";
import { LiveSessionCardLive } from "../UpcomingLive/LiveLandingPageWrapper";
import { useReminders } from "../UpcomingLive/useReminders";
import type { ReminderService } from "../UpcomingLive/UpcomingLive";
import type { LiveStream as UpcomingLiveStream } from "../../types/upcoming-live.types";
import type { MediaData } from "../../types/livelanding.types";
import { __LIVE_LANDING_PAGE__ } from "../../constants/livelanding-page.constants";
import { defaultReminderService } from "../../utils/reminderServiceAdapter";
import { logReminderInfo } from "../../utils/reminderLogger";

const DesktopSkeleton = ({ count = 6 }: { count?: number }) => (
  <div className="flex [scrollbar-width:none] gap-[8px] overflow-x-auto [&::-webkit-scrollbar]:hidden">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={`desktop-skeleton-${i}`}
        className="min-w-[280px] shrink-0 sm:min-w-[310px] md:min-w-[320px] lg:min-w-[343px]"
      >
        <Skeleton
          variant="rectangular"
          className="mb-2 h-[200px] w-full rounded-[8px] bg-gray-200"
          aria-hidden="true"
        />
        <Skeleton variant="text" className="mb-1 h-[14px] w-[75%] bg-gray-200" aria-hidden="true" />
        <Skeleton variant="text" className="h-[12px] w-[50%] bg-gray-200" aria-hidden="true" />
      </div>
    ))}
  </div>
);

const UpcomingDesktopSkeleton = ({ count, ariaLabel }: { count: number; ariaLabel: string }) => (
  <DynamicSkeletonLoader
    className="mt-4 hidden [scrollbar-width:none] gap-4 overflow-x-auto max-[768px]:hidden min-[421px]:flex [&::-webkit-scrollbar]:hidden"
    aria-busy="true"
    aria-live="polite"
    aria-label={ariaLabel}
  >
    <span className="sr-only">Loading {ariaLabel} sessions, please wait...</span>
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={`upcoming-skeleton-${i}`}
        className="min-w-[320px] sm:min-w-[300px] md:min-w-[310px] lg:min-w-[320px]"
      >
        <Skeleton
          variant="rectangular"
          className="mb-3 h-[200px] rounded-lg bg-gray-200 sm:h-[190px] sm:max-w-[343px] md:h-[200px] md:max-w-[768px] lg:h-[200px]"
          aria-hidden="true"
        />
      </div>
    ))}
  </DynamicSkeletonLoader>
);

export interface LivePlaylistDesktopBlockProps {
  streams: MediaData["live_streams"];
  isUpcoming: boolean;
  loading: boolean;
  enableSuspense: boolean;
  displayTitle: string;
  emptyMessage: string;
  title: string;
  loadingLabel: string;
  setReminderText: string;
  reminderSetText: string;
  sectionTitle?: string;
  sectionTitleCt?: string;
  influencerId?: string;
  reminderService?: ReminderService;
  /** Called when horizontal scroll reaches near the end (playlist mode only) */
  onScrollEnd?: () => void;
  /** Whether a next-page fetch is in progress */
  isFetchingMore?: boolean;
  /** Whether there are more pages to fetch */
  hasMore?: boolean;
}

const LivePlaylistDesktopBlockComponent = ({
  streams,
  isUpcoming,
  loading,
  enableSuspense,
  displayTitle,
  emptyMessage,
  title,
  loadingLabel,
  setReminderText,
  reminderSetText,
  sectionTitle,
  sectionTitleCt,
  influencerId,
  reminderService: reminderServiceProp,
  onScrollEnd,
  isFetchingMore = false,
  hasMore = false,
}: LivePlaylistDesktopBlockProps) => {
  const scrollRef = useRef<HTMLUListElement | null>(null);

  const reminderService = useMemo(() => {
    if (!isUpcoming) return undefined;
    return reminderServiceProp ?? defaultReminderService;
  }, [isUpcoming, reminderServiceProp]);

  const { reminders, handleReminderToggle } = useReminders(
    reminderService,
    isUpcoming ? (influencerId ?? __LIVE_LANDING_PAGE__) : undefined
  );

  useEffect(() => {
    if (!isUpcoming) return;
    logReminderInfo("LivePlaylistDesktopBlock", "reminders:map-updated", {
      isUpcoming,
      streamsCount: streams.length,
      remindersSize: reminders.size,
      reminderVideoIds: Array.from(reminders.keys()),
      reminders,
    });
  }, [isUpcoming, reminders, streams.length]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !onScrollEnd || !hasMore) return;
    const threshold = 150; // px from right edge to trigger load
    if (el.scrollLeft + el.clientWidth >= el.scrollWidth - threshold) {
      onScrollEnd();
    }
  }, [onScrollEnd, hasMore]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || isUpcoming) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll, isUpcoming]);

  const PlaylistContent = isUpcoming ? (
    <ul
      id="upcoming-live-list-desktop"
      className="hidden list-none md:block"
      aria-label="Upcoming live sessions carousel"
      aria-live="polite"
    >
      <BFLCarouselNew
        slidesPerView={5.1}
        showDots={false}
        showNavigation={true}
        maintainSlidesOnMobile={true}
        gap={8}
      >
        {streams.map((session, index) => {
          const reminder = reminders.get(session.id);

          logReminderInfo("LivePlaylistDesktopBlock", "reminders:session->card-props", {
            sessionId: session.id,
            hasReminder: Boolean(reminder),
            reminderIsActive: reminder?.isActive,
            reminder,
          });
          return (
            <li key={String(session.encoded_id ?? session.id ?? index)} className="list-none">
              <LiveSessionCardLive
                session={session as unknown as UpcomingLiveStream}
                bordernone="border-none"
                setReminderText={setReminderText}
                reminderSetText={reminderSetText}
                isReminderSet={Boolean(reminder)}
                onReminderToggle={handleReminderToggle}
                reminderService={reminderService}
                title={sectionTitle}
                titleCt={sectionTitleCt}
              />
            </li>
          );
        })}
      </BFLCarouselNew>
    </ul>
  ) : (
    <ul
      ref={scrollRef}
      className="flex [scrollbar-width:none] gap-[8px] overflow-x-auto scroll-smooth [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      aria-live="polite"
    >
      {streams.map((session) => (
        <li key={session.id} className="shrink-0">
          <LiveSessionCardLive
            session={session as unknown as UpcomingLiveStream}
            bordernone="border-none"
            isFeaturedLiveCard
            enableDynamicPlayer
            title={sectionTitle}
            titleCt={sectionTitleCt}
          />
        </li>
      ))}
      {isFetchingMore && (
        <li
          className="min-w-[280px] shrink-0 sm:min-w-[310px] md:min-w-[320px] lg:min-w-[343px]"
          aria-live="polite"
          aria-label="Loading more videos"
        >
          <Skeleton
            variant="rectangular"
            className="mb-2 h-[200px] w-full rounded-[8px] bg-gray-200"
            aria-hidden="true"
          />
          <Skeleton
            variant="text"
            className="mb-1 h-[14px] w-[75%] bg-gray-200"
            aria-hidden="true"
          />
          <Skeleton variant="text" className="h-[12px] w-[50%] bg-gray-200" aria-hidden="true" />
        </li>
      )}
    </ul>
  );

  const renderDesktopBlockContent = () => {
    if (loading) {
      if (isUpcoming && streams.length > 0) {
        return <UpcomingDesktopSkeleton count={streams.length} ariaLabel={displayTitle} />;
      }
      if (!isUpcoming) {
        return (
          <DynamicSkeletonLoader aria-busy="true" aria-label={loadingLabel}>
            <span className="sr-only">Loading {title} videos, please wait...</span>
            <DesktopSkeleton />
          </DynamicSkeletonLoader>
        );
      }
      return null;
    }

    if (!isUpcoming && enableSuspense) {
      return (
        <Suspense
          fallback={
            <DynamicSkeletonLoader aria-busy="true" aria-label={loadingLabel}>
              <span className="sr-only">Loading {title} videos, please wait...</span>
              <DesktopSkeleton />
            </DynamicSkeletonLoader>
          }
        >
          {streams.length > 0 ? (
            PlaylistContent
          ) : (
            <output className="mt-4 block text-center text-gray-500" aria-live="polite">
              {emptyMessage}
            </output>
          )}
        </Suspense>
      );
    }

    if (streams.length > 0) {
      return PlaylistContent;
    }

    return (
      <output className="mt-4 block text-center text-gray-500" aria-live="polite">
        {emptyMessage}
      </output>
    );
  };

  return <div className="hidden md:block">{renderDesktopBlockContent()}</div>;
};

export const LivePlaylistDesktopBlock = memo(LivePlaylistDesktopBlockComponent);
